// The practice's Supplement library (#/supplements): Designs for Health products
// and what each one supports for everyday wellness. Pure and browser safe: the
// importer (scripts/import-supplement-library.mjs), validate-content, the review
// page, the API and the member app all use these same rules and words.
//
// Legal frame (owner, pending attorney review): structure/function wording and
// common minor life stage symptoms only, never a disease or condition. No product
// is ever tied to a condition anywhere, and the library never appears inside
// lessons, practices, challenges, the library of practices, Marie or condition content.

/** Member facing categories, in display order. */
export const LIBRARY_CATEGORIES = Object.freeze([
  'Sleep and rest', 'Stress and mood', 'Digestion', 'Energy', 'Monthly cycle', 'Menopause', 'Everyday essentials', 'Movement and recovery',
]);

/**
 * Disease and claim words a supports statement, a caution or a product name may never use.
 * Case insensitive, whole words (a phrase matches as a whole phrase). One list, tested.
 */
export const CLAIM_BLOCKLIST = Object.freeze([
  'treat', 'treats', 'treatment', 'cure', 'cures', 'heal', 'heals', 'healing', 'prevent', 'prevents', 'prevention', 'diagnose',
  'reverse', 'reverses', 'remedy', 'fight', 'fights', 'disease', 'disorder', 'syndrome', 'condition', 'illness', 'infection',
  'inflammation', 'inflammatory', 'pain relief', 'PCOS', 'polycystic', 'diabetes', 'diabetic', 'prediabetes', 'insulin resistance',
  'blood sugar problems', 'hypertension', 'high blood pressure', 'blood pressure', 'cholesterol', 'heart disease', 'lupus',
  'autoimmune', 'arthritis', 'osteoporosis', 'osteopenia', 'thyroid', 'hypothyroid', 'Hashimoto', 'depression', 'depressive',
  'anxiety disorder', 'anxiety', 'postpartum', 'PMS', 'premenstrual syndrome', 'PMDD', 'insomnia', 'IBS', 'irritable bowel', 'SIBO',
  'leaky gut', 'candida', 'cancer', 'tumor', 'migraine', 'ADHD', 'dementia', 'Alzheimer', 'fibromyalgia', 'endometriosis',
  'fertility', 'infertility', 'obesity', 'weight loss', 'detox', 'cleanse', 'immune boost', 'boost immunity',
]);

export const SUPPORTS_MAX_WORDS = 25;
export const CAUTION_MAX_WORDS = 25;

/** Shown on every product card, whatever the row says. */
export const STANDARD_CAUTION = 'Check with your provider before you start this if you are pregnant or breastfeeding, take any medicines, or have a health condition.';
export const FDA_STATEMENT = '*These statements have not been evaluated by the Food and Drug Administration. These products are not intended to diagnose, treat, cure or prevent any disease.';
export const EARN_NOTE = 'Our practice may earn from purchases.';
export const LIBRARY_INTRO = 'Designs for Health products our practice likes to suggest, and what each one supports for everyday wellness. Supplements are not drugs. Browse, and choose what fits you, ideally with your healthcare provider.';
export const DFH_HOME = 'https://www.designsforhealth.com/products/';

const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Whole word, case insensitive; spaces in a phrase match any run of spaces. */
const BLOCK_RES = CLAIM_BLOCKLIST.map((w) => [w, new RegExp(`(^|[^\\p{L}\\p{N}])${escRe(w).replace(/ /g, '\\s+')}(?=$|[^\\p{L}\\p{N}])`, 'iu')]);

/** Every blocklisted word or phrase in the text. */
export function blockedTerms(text) {
  const s = String(text ?? '');
  return BLOCK_RES.filter(([, re]) => re.test(s)).map(([w]) => w);
}

const DASH = /[‐-―−﹘﹣－-]/;
const DOSE = [
  /\b\d[\d,.]*\s*(mg|mcg|µg|ug|iu|g|grams?|milligrams?|micrograms?|cfu|billion|million|capsules?|caps|tablets?|softgels?|scoops?|drops?|servings?)\b/i,
  /(^|[^\p{L}])(mg|mcg|µg|iu|grams?|milligrams?|micrograms?|cfu|billion)(?=$|[^\p{L}])/iu,
];
const RELIEF = /\b(relief|relieve[sd]?|relieving)\b/i;
const MILD = /\b(occasional|occasionally|mild)\b/i;
const words = (s) => String(s ?? '').trim().split(/\s+/).filter(Boolean).length;

/** Problems with a member facing statement (supports or cautions). `field` names it in the messages. */
export function statementProblems(text, field, maxWords) {
  const s = String(text ?? '').trim();
  const out = [];
  if (DASH.test(s)) out.push(`${field} has a dash (no dashes of any kind)`);
  if (s.includes(';')) out.push(`${field} has a semicolon`);
  if (DOSE.some((re) => re.test(s))) out.push(`${field} has a dose or amount (mg, mcg, IU, grams, CFU, billion)`);
  const hits = blockedTerms(s);
  if (hits.length) out.push(`${field} uses a disease or claim word: ${hits.join(', ')}`);
  if (RELIEF.test(s) && !MILD.test(s)) out.push(`${field} says relief without "occasional" or "mild"`);
  if (/[<>]/.test(s)) out.push(`${field} still has a <placeholder>`);
  const n = words(s);
  if (n > maxWords) out.push(`${field} has ${n} words (${maxWords} at most)`);
  return out;
}

/** https on designsforhealth.com (or www. and other subdomains), no login in the address. */
export function dfhUrlProblem(url, field) {
  let u;
  try { u = new URL(String(url ?? '').trim()); } catch { return `${field} is not a web address`; }
  const h = u.hostname.toLowerCase();
  if (u.protocol !== 'https:') return `${field} must start with https://`;
  if (u.username || u.password) return `${field} must not carry a login`;
  if (!(h === 'designsforhealth.com' || h.endsWith('.designsforhealth.com'))) return `${field} must be on designsforhealth.com (got ${h})`;
  return null;
}

/** The category as written in the list, or null. Case and spacing insensitive. */
export function canonicalCategory(c) {
  const k = String(c ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  return LIBRARY_CATEGORIES.find((x) => x.toLowerCase() === k) ?? null;
}

/**
 * Every problem with one product, messages named by CSV column.
 * p: { productName, productUrl, category, supports, sourceUrl, cautions }
 */
export function productProblems(p) {
  const out = [];
  const name = String(p?.productName ?? '').trim();
  if (!name) out.push('product_name is empty');
  else {
    if (/[<>]/.test(name)) out.push('product_name still has a <placeholder>');
    if (name.length > 80) out.push('product_name is longer than 80 characters');
    const hits = blockedTerms(name);
    if (hits.length) out.push(`product_name uses a disease or claim word: ${hits.join(', ')}`);
  }
  for (const [k, f] of [['productUrl', 'product_url'], ['sourceUrl', 'source_url']]) {
    const e = dfhUrlProblem(p?.[k], f);
    if (e) out.push(e);
  }
  if (!canonicalCategory(p?.category)) out.push(`category must be one of: ${LIBRARY_CATEGORIES.join(', ')} (got "${String(p?.category ?? '')}")`);
  const supports = String(p?.supports ?? '').trim();
  if (!supports) out.push('supports is empty');
  else out.push(...statementProblems(supports, 'supports', SUPPORTS_MAX_WORDS));
  const cautions = String(p?.cautions ?? '').trim();
  if (cautions) out.push(...statementProblems(cautions, 'cautions', CAUTION_MAX_WORDS));
  return out;
}

/** A stable, readable slug for a product name. */
export function productSlug(name) {
  return String(name ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48) || 'product';
}

/** Member view: never the source_url. Products that fail any rule are dropped (the importer and validate-content refuse them first). */
export function memberProducts(file) {
  const list = Array.isArray(file?.products) ? file.products : [];
  return list.filter((p) => productProblems(p).length === 0).map((p) => ({
    id: productSlug(p.productName),
    name: String(p.productName).trim(),
    url: String(p.productUrl).trim(),
    category: canonicalCategory(p.category),
    supports: String(p.supports).trim(),
    caution: String(p.cautions ?? '').trim() || null,
  }));
}
